/**
 * Centralized date/time formatting utility
 * Uses browser's timezone (Intl API) - automatically respects user's location
 */

/**
 * Get user's timezone from browser
 * @returns {string} Timezone identifier (e.g., "America/New_York")
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Parse a date string from the server (treats as UTC if no timezone specified)
 * Server timestamps are stored in UTC but may not include the 'Z' suffix
 * @param {Date|string} date - Date object or date string from server
 * @returns {Date} Date object
 */
const parseServerDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return date;

  // If string doesn't have timezone info (Z or +/-), treat as UTC
  const dateStr = String(date);
  if (!dateStr.endsWith('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
    // Add 'Z' to indicate UTC
    return new Date(dateStr + 'Z');
  }
  return new Date(dateStr);
};

/**
 * Format date/time for display (respects user's locale and timezone)
 * @param {Date|string} date - Date object or ISO string
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date/time string
 */
export const formatDateTime = (date, options = {}) => {
  if (!date) return 'Unknown';
  const d = parseServerDate(date);
  if (!d || isNaN(d.getTime())) return 'Unknown';

  const defaultOptions = {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...options
  };

  return new Intl.DateTimeFormat(undefined, defaultOptions).format(d);
};

/**
 * Format date only (no time)
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return 'Unknown';
  const d = parseServerDate(date);
  if (!d || isNaN(d.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(d);
};

/**
 * Format time only (no date)
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  if (!date) return 'Unknown';
  const d = parseServerDate(date);
  if (!d || isNaN(d.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, { timeStyle: 'short' }).format(d);
};

/**
 * Format for log messages with timezone indicator [HH:MM:SS AM/PM TZ]
 * @param {Date} date - Date object (defaults to now)
 * @returns {string} Formatted log timestamp
 */
export const formatLogTime = (date = new Date()) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  }).format(d);
};

/**
 * Format relative time (e.g., "5 minutes ago")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Relative time string
 */
export const formatTimeAgo = (date) => {
  if (!date) return 'Unknown';
  const d = parseServerDate(date);
  if (!d || isNaN(d.getTime())) return 'Unknown';

  const now = new Date();
  const diffInSeconds = Math.floor((now - d) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }

  return formatDate(d);
};

/**
 * Format for file names (timezone-safe, no special characters)
 * @param {Date} date - Date object (defaults to now)
 * @returns {string} Filename-safe timestamp (e.g., "20260114_103045")
 */
export const formatForFilename = (date = new Date()) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
};

/**
 * Format for API requests (YYYY-MM-DD)
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} ISO date string (YYYY-MM-DD)
 */
export const formatForAPI = (date) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().split('T')[0];
};

/**
 * Format duration between two dates
 * @param {Date|string} startTime - Start time
 * @param {Date|string} endTime - End time (defaults to now)
 * @returns {string} Formatted duration (e.g., "2m 30s")
 */
export const formatDuration = (startTime, endTime = new Date()) => {
  if (!startTime) return '';
  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  const end = typeof endTime === 'string' ? new Date(endTime) : endTime;

  const diff = Math.floor((end - start) / 1000);
  if (diff < 0) return '0s';
  if (diff < 60) return `${diff}s`;

  const mins = Math.floor(diff / 60);
  const secs = diff % 60;

  if (mins < 60) return `${mins}m ${secs}s`;

  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
};
